import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Users,
  Edit2,
  Trash2,
  Mail,
  Phone,
  Building2,
  MapPin,
  Bell,
  Send,
} from 'lucide-react';

// Follow-up Dialog Component
const FollowUpDialog = ({ client, open, onOpenChange }) => {
  const { token } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedQuote, setSelectedQuote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);

  // Fetch template when dialog opens
  React.useEffect(() => {
    if (open && client) {
      // Fetch client's quotes
      fetch(`${API_URL}/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.json())
        .then(data => {
          const clientQuotes = data.filter(q => q.client_id === client.id);
          setQuotes(clientQuotes);
        })
        .catch(() => setQuotes([]));

      // Fetch template
      fetch(`${API_URL}/clients/${client.id}/follow-up-template`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.json())
        .then(data => {
          setSubject(data.subject);
          setBody(data.body);
        })
        .catch(console.error);
    }
  }, [open, client, token]);

  // Update template when quote is selected
  const handleQuoteChange = async (quoteId) => {
    setSelectedQuote(quoteId);
    if (quoteId && quoteId !== 'none') {
      try {
        const res = await fetch(`${API_URL}/clients/${client.id}/follow-up-template?quote_id=${quoteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setSubject(data.subject);
        setBody(data.body);
      } catch (error) {
        console.error('Failed to update template:', error);
      }
    }
  };

  const handleSend = async () => {
    if (!subject || !body) {
      toast.error('Sujet et message requis');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/clients/${client.id}/follow-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: client.id,
          quote_id: selectedQuote !== 'none' ? selectedQuote : null,
          subject,
          body,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur lors de l\'envoi');
      }

      const result = await res.json();
      toast.success('Email de relance envoyé');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Relancer {client.name}
          </DialogTitle>
          <DialogDescription>
            Envoyez un email de relance au client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">Destinataire</p>
            <p className="font-medium">{client.email || 'Pas d\'email configuré'}</p>
          </div>

          {quotes.length > 0 && (
            <div className="space-y-2">
              <Label>Devis associé (optionnel)</Label>
              <Select value={selectedQuote} onValueChange={handleQuoteChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un devis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun devis</SelectItem>
                  {quotes.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.reference} - {q.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Sujet</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de l'email"
              data-testid="followup-subject"
            />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Contenu de l'email..."
              data-testid="followup-body"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={isLoading || !client.email}
              data-testid="send-followup-btn"
            >
              {isLoading ? (
                'Envoi...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Envoyer
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ClientCard = ({ client, onEdit, onDelete, onFollowUp, canEdit }) => {
  return (
    <Card className="card-hover group" data-testid={`client-card-${client.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-lg font-bold text-primary">
              {client.name.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium">{client.name}</h3>
                {client.company && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {client.company}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatDate(client.created_at)}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="flex items-center gap-1 hover:text-primary transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> {client.email}
                </a>
              )}
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="flex items-center gap-1 hover:text-primary transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" /> {client.phone}
                </a>
              )}
              {client.address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {client.address}
                </span>
              )}
            </div>

            {client.notes && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {client.notes}
              </p>
            )}

            <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
              {client.email && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onFollowUp(client)}
                  data-testid={`followup-client-${client.id}`}
                >
                  <Bell className="w-4 h-4 mr-1" /> Relancer
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(client)}
                  data-testid={`edit-client-${client.id}`}
                >
                  <Edit2 className="w-4 h-4 mr-1" /> Modifier
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => onDelete(client.id)}
                  data-testid={`delete-client-${client.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientFormDialog = ({ client, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState(
    client || {
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      notes: '',
    }
  );

  React.useEffect(() => {
    if (client) {
      setFormData(client);
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        notes: '',
      });
    }
  }, [client, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {client ? 'Modifier le client' : 'Nouveau client'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="client-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="client-phone-input"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="company">Entreprise</Label>
              <Input
                id="company"
                value={formData.company || ''}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="client-submit-btn">
              {client ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ClientsPage = () => {
  const { token, can } = useAuthStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [followUpClient, setFollowUpClient] = useState(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const canEditClients = can('can_edit_clients');

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch clients');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create client');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client créé avec succès');
      setDialogOpen(false);
    },
    onError: () => toast.error('Erreur lors de la création'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update client');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client mis à jour');
      setDialogOpen(false);
      setEditingClient(null);
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete client');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client supprimé');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const handleSave = (data) => {
    if (editingClient) {
      updateMutation.mutate({ id: editingClient.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleFollowUp = (client) => {
    setFollowUpClient(client);
    setFollowUpOpen(true);
  };

  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.company || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Clients</h1>
          <p className="text-muted-foreground">Gérez votre base clients</p>
        </div>
        {canEditClients && (
          <Button
            onClick={() => {
              setEditingClient(null);
              setDialogOpen(true);
            }}
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau client
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un client..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="client-search"
        />
      </div>

      {/* Clients List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredClients.length > 0 ? (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              canEdit={canEditClients}
              onEdit={(c) => {
                setEditingClient(c);
                setDialogOpen(true);
              }}
              onDelete={(id) => {
                if (window.confirm('Supprimer ce client ?')) {
                  deleteMutation.mutate(id);
                }
              }}
              onFollowUp={handleFollowUp}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucun client trouvé</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'Essayez avec d\'autres termes' : 'Ajoutez votre premier client'}
            </p>
            <Button
              onClick={() => {
                setEditingClient(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Créer un client
            </Button>
          </CardContent>
        </Card>
      )}

      <ClientFormDialog
        client={editingClient}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingClient(null);
        }}
        onSave={handleSave}
      />

      <FollowUpDialog
        client={followUpClient}
        open={followUpOpen}
        onOpenChange={(open) => {
          setFollowUpOpen(open);
          if (!open) setFollowUpClient(null);
        }}
      />
    </div>
  );
};

export default ClientsPage;
